import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Package, Scan, Search, CheckCircle, AlertCircle, X, Plus, FileText,
  Smartphone, QrCode, Printer, ChevronRight, User, Trash2, Check,
  RefreshCw, Camera, Box, AlertTriangle, Upload, Building, Truck,
  ShoppingCart, Wrench, Store, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

// Source type icons
const SOURCE_ICONS = {
  BUYBACK: { icon: User, color: 'bg-blue-500' },
  CUSTOMER_RETURNS: { icon: ShoppingCart, color: 'bg-green-500' },
  CONSUMER_RETURNS_SOS: { icon: Wrench, color: 'bg-orange-500' },
  BULK_PURCHASE: { icon: Package, color: 'bg-purple-500' },
  FLIPKART_VRP: { icon: Truck, color: 'bg-yellow-500' },
  ZOMART_EXCHANGE: { icon: Store, color: 'bg-pink-500' },
  STORE_TRANSFER: { icon: Building, color: 'bg-teal-500' },
};

function CreateGRN({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Select Source, 2: Add Devices / Select Manifest, 3: Review & Submit
  const [sources, setSources] = useState([]);
  const [storeTypes, setStoreTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Form state
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceReference, setSourceReference] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [selectedStoreType, setSelectedStoreType] = useState('');
  const [devices, setDevices] = useState([]);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [grnResult, setGrnResult] = useState(null);
  
  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  
  // Buyback-specific state
  const [availableManifests, setAvailableManifests] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [manifestSearchTerm, setManifestSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  
  // Device form for non-buyback sources
  const [deviceForm, setDeviceForm] = useState({
    imei: '',
    imei2: '',
    serial_number: '',
    brand: '',
    model: '',
    category: 'Mobile Phones',
    variant: '',
    condition: '',
    grade: '',
    purchase_price: '',
    remarks: ''
  });

  // Scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scanField, setScanField] = useState('imei');
  const [scanningManifest, setScanningManifest] = useState(false);

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/grn/sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSources(data.sources);
        setStoreTypes(data.store_types);
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error loading sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadManifests = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get manifests that are RECEIVED but not yet GRN completed
      const response = await fetch(`${API_URL}/api/warehouse/manifests?status=RECEIVED`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        // Filter only those without GRN
        const pendingManifests = (data.manifests || []).filter(m => 
          m.status === 'RECEIVED' && !m.grn_completed
        );
        setAvailableManifests(data.manifests || []);
      }
    } catch (error) {
      console.error('Error loading manifests:', error);
    }
  };

  const handleSourceSelect = async (source) => {
    setSelectedSource(source);
    
    if (source.value === 'BUYBACK') {
      // Load manifests for buyback
      await loadManifests();
    }
    setStep(2);
  };

  const handleManifestSelect = (manifest) => {
    setSelectedManifest(manifest);
    setSourceReference(manifest.manifest_id);
    // Auto-select all leads from manifest
    const leadIds = manifest.leads?.map(l => l.lead_id) || [];
    setSelectedLeads(leadIds);
    // Convert leads to device format
    const manifestDevices = (manifest.leads || []).map(lead => ({
      id: lead.lead_id,
      lead_id: lead.lead_id,
      imei: lead.imei || null,
      brand: lead.device_brand,
      model: lead.device_model,
      category: 'Mobile Phones',
      customer_name: lead.customer_name,
      final_price: lead.final_price,
      pq_grade: lead.pq_grade,
      fromManifest: true
    }));
    setDevices(manifestDevices);
  };

  const handleManifestScan = async (scannedValue) => {
    // Find manifest by ID
    const manifest = availableManifests.find(m => 
      m.manifest_id === scannedValue || 
      m.manifest_id.includes(scannedValue)
    );
    
    if (manifest) {
      handleManifestSelect(manifest);
      setScanningManifest(false);
    } else {
      alert('Manifest not found. Please check the ID and try again.');
    }
  };

  const handleAddDevice = () => {
    if (!deviceForm.brand || !deviceForm.model) {
      alert('Please enter at least Brand and Model');
      return;
    }
    
    setDevices([...devices, { 
      ...deviceForm, 
      purchase_price: parseFloat(deviceForm.purchase_price) || 0,
      id: Date.now() 
    }]);
    setDeviceForm({
      imei: '',
      imei2: '',
      serial_number: '',
      brand: '',
      model: '',
      category: 'Mobile Phones',
      variant: '',
      condition: '',
      grade: '',
      purchase_price: '',
      remarks: ''
    });
    setShowAddDevice(false);
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !sourceReference) {
      alert('Please select a file and enter source reference');
      return;
    }
    
    setProcessing(true);
    setUploadErrors([]);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('source_type', selectedSource.value);
      formData.append('source_reference', sourceReference);
      formData.append('receiver_id', user?.employee_id || user?.id || 'WH-USER');
      formData.append('receiver_name', user?.name || 'Warehouse User');
      if (vendorName) formData.append('vendor_name', vendorName);
      if (selectedStoreType) formData.append('store_type', selectedStoreType);
      
      const response = await fetch(`${API_URL}/api/warehouse/grn/bulk-upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrnResult({
          grn: { grn_id: data.grn_id },
          device_ids: data.device_ids
        });
        if (data.errors && data.errors.length > 0) {
          setUploadErrors(data.errors);
        }
        setStep(3);
      } else {
        alert(`Error: ${data.detail || data.message || 'Upload failed'}`);
        if (data.errors) {
          setUploadErrors(data.errors);
        }
      }
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Network error. Please try again.');
    } finally {
      setProcessing(false);
      setShowBulkUpload(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/warehouse/grn/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Create and download CSV
        const blob = new Blob([data.template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'grn_bulk_upload_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleRemoveDevice = (id) => {
    setDevices(devices.filter(d => d.id !== id));
    if (selectedManifest) {
      setSelectedLeads(selectedLeads.filter(lid => lid !== id));
    }
  };

  const toggleLeadSelection = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleSubmitGRN = async () => {
    if (selectedSource?.value === 'BUYBACK') {
      if (!selectedManifest) {
        alert('Please select a manifest');
        return;
      }
      if (selectedLeads.length === 0) {
        alert('Please select at least one device');
        return;
      }
    } else {
      if (devices.length === 0) {
        alert('Please add at least one device');
        return;
      }
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      if (selectedSource?.value === 'BUYBACK') {
        // Use existing GRN create endpoint for buyback
        const response = await fetch(`${API_URL}/api/warehouse/grn/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            manifest_id: selectedManifest.manifest_id,
            received_lead_ids: selectedLeads,
            receiver_id: user?.employee_id || user?.id || 'WH-USER',
            receiver_name: user?.name || 'Warehouse User'
          })
        });
        
        const data = await response.json();
        
        if (data.success || response.ok) {
          // Generate DIDs for received leads
          const didResponse = await fetch(
            `${API_URL}/api/warehouse/did/generate-bulk?grn_id=${data.grn?.grn_id}`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          const didData = await didResponse.json();
          
          setGrnResult({
            grn: data.grn,
            device_ids: didData.device_ids || [],
            manifest: selectedManifest
          });
          setStep(3);
        } else {
          alert(`Error: ${data.detail || 'Failed to create GRN'}`);
        }
      } else {
        // Use unified GRN for other sources
        const requestBody = {
          source_type: selectedSource.value,
          devices: devices.map(d => ({
            imei: d.imei || null,
            imei2: d.imei2 || null,
            serial_number: d.serial_number || null,
            brand: d.brand,
            model: d.model,
            category: d.category,
            variant: d.variant || null,
            condition: d.condition || null,
            grade: d.grade || null,
            purchase_price: d.purchase_price || null,
            remarks: d.remarks || null
          })),
          receiver_id: user?.employee_id || user?.id || 'WH-USER',
          receiver_name: user?.name || 'Warehouse User',
          warehouse_id: 'WH-001',
          vendor_name: vendorName || null
        };
        
        // Add source-specific fields
        if (selectedSource.requires === 'ticket_id') {
          requestBody.ticket_id = sourceReference;
        } else if (selectedSource.requires === 'po_number') {
          requestBody.po_number = sourceReference;
        } else if (selectedSource.requires === 'awb_number') {
          requestBody.awb_number = sourceReference;
        } else if (selectedSource.requires === 'store_id') {
          requestBody.store_id = sourceReference;
          requestBody.store_type = selectedStoreType;
        }
        requestBody.source_reference = sourceReference;
        
        const response = await fetch(`${API_URL}/api/warehouse/grn/unified`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
        
        const data = await response.json();
        
        if (data.success) {
          setGrnResult(data);
          setStep(3);
        } else {
          alert(`Error: ${data.detail || 'Failed to create GRN'}`);
        }
      }
    } catch (error) {
      console.error('Error creating GRN:', error);
      alert('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleScan = (value) => {
    setDeviceForm({ ...deviceForm, [scanField]: value });
    setShowScanner(false);
  };

  // Filtered manifests
  const filteredManifests = availableManifests.filter(m => 
    !manifestSearchTerm || 
    m.manifest_id.toLowerCase().includes(manifestSearchTerm.toLowerCase()) ||
    m.engineer_name?.toLowerCase().includes(manifestSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2b3380] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>Create GRN</h1>
              <p className="text-sm text-gray-500">
                Step {step} of 3: {step === 1 ? 'Select Source' : step === 2 ? (selectedSource?.value === 'BUYBACK' ? 'Select Manifest' : 'Add Devices') : 'Complete'}
              </p>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s <= step ? 'bg-[#2b3380] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${s < step ? 'bg-[#2b3380]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          {/* Step 1: Select Source */}
          {step === 1 && (
            <div data-testid="step-1-sources">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Select GRN Source</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sources.map((source) => {
                  const IconConfig = SOURCE_ICONS[source.value] || { icon: Package, color: 'bg-gray-500' };
                  const Icon = IconConfig.icon;
                  
                  return (
                    <div
                      key={source.value}
                      onClick={() => handleSourceSelect(source)}
                      className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-[#2b3380]/50 hover:shadow-md transition-all"
                      data-testid={`source-${source.value}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${IconConfig.color} text-white`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{source.label}</h3>
                          <p className="text-sm text-gray-500">
                            {source.value === 'BUYBACK' ? 'Auto-fill from Engineer Manifest' : `Requires: ${source.requires.replace('_', ' ')}`}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Buyback - Select Manifest */}
          {step === 2 && selectedSource?.value === 'BUYBACK' && (
            <div data-testid="step-2-buyback">
              {/* Source Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <User className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Buyback GRN</p>
                    <p className="text-sm text-blue-600">
                      Select a manifest or scan Manifest ID. Device data will be auto-populated from Engineer QC.
                    </p>
                  </div>
                </div>
              </div>

              {/* Manifest Search/Scan */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={manifestSearchTerm}
                      onChange={(e) => setManifestSearchTerm(e.target.value)}
                      placeholder="Search by Manifest ID or Engineer Name..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      data-testid="manifest-search"
                    />
                  </div>
                  <button
                    onClick={() => setScanningManifest(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#232a66]"
                    data-testid="scan-manifest-btn"
                  >
                    <Scan className="w-4 h-4" />
                    Scan Manifest
                  </button>
                </div>

                {/* Available Manifests */}
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {filteredManifests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No pending manifests found</p>
                      <p className="text-sm">Manifests appear here after engineer dispatch</p>
                    </div>
                  ) : (
                    filteredManifests.map((manifest) => (
                      <div
                        key={manifest.manifest_id}
                        onClick={() => handleManifestSelect(manifest)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedManifest?.manifest_id === manifest.manifest_id
                            ? 'border-[#2b3380] bg-[#2b3380]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`manifest-${manifest.manifest_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{manifest.manifest_id}</p>
                            <p className="text-sm text-gray-500">
                              {manifest.engineer_name} • {manifest.lead_count} devices • ₹{manifest.total_value?.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(manifest.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            manifest.status === 'RECEIVED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {manifest.status}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Selected Manifest Devices */}
              {selectedManifest && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">
                      Devices from Manifest ({selectedManifest.leads?.length || 0})
                    </h3>
                    <button
                      onClick={() => setSelectedLeads(selectedManifest.leads?.map(l => l.lead_id) || [])}
                      className="text-sm text-[#2b3380] hover:underline"
                    >
                      Select All
                    </button>
                  </div>

                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {(selectedManifest.leads || []).map((lead, idx) => (
                      <div
                        key={lead.lead_id}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedLeads.includes(lead.lead_id)
                            ? 'border-[#2b3380] bg-[#2b3380]/5'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.lead_id)}
                            onChange={() => toggleLeadSelection(lead.lead_id)}
                            className="mt-1 w-5 h-5 text-[#2b3380] rounded focus:ring-[#2b3380]"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-800">
                                {lead.device_brand} {lead.device_model}
                              </p>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                lead.pq_grade?.startsWith('Z1') || lead.pq_grade?.startsWith('Z2') 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                Grade: {lead.pq_grade || 'N/A'}
                              </span>
                            </div>
                            <div className="mt-1 grid grid-cols-2 gap-2 text-sm text-gray-500">
                              <span>Lead ID: {lead.lead_id}</span>
                              <span>Customer: {lead.customer_name}</span>
                              <span>IMEI: {lead.imei || 'Not captured'}</span>
                              <span className="font-semibold text-green-600">₹{lead.final_price?.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Selected: {selectedLeads.length} of {selectedManifest.leads?.length || 0} devices</span>
                      <span className="font-semibold text-gray-800">
                        Total: ₹{(selectedManifest.leads || [])
                          .filter(l => selectedLeads.includes(l.lead_id))
                          .reduce((sum, l) => sum + (l.final_price || 0), 0)
                          .toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setStep(1);
                    setSelectedManifest(null);
                    setSelectedLeads([]);
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmitGRN}
                  disabled={!selectedManifest || selectedLeads.length === 0 || processing}
                  className="flex-1 py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="create-buyback-grn-btn"
                >
                  {processing ? 'Creating GRN...' : `Create GRN (${selectedLeads.length} devices)`}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Other Sources - Add Devices */}
          {step === 2 && selectedSource?.value !== 'BUYBACK' && (
            <div data-testid="step-2-other-devices">
              {/* Source Info */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${SOURCE_ICONS[selectedSource?.value]?.color || 'bg-gray-500'} text-white`}>
                      {React.createElement(SOURCE_ICONS[selectedSource?.value]?.icon || Package, { className: 'w-5 h-5' })}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{selectedSource?.label}</p>
                      <p className="text-sm text-gray-500">{devices.length} device(s) added</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-[#2b3380] hover:underline"
                  >
                    Change Source
                  </button>
                </div>
                
                {/* Source Reference Input */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {selectedSource?.requires?.replace('_', ' ').toUpperCase()} *
                    </label>
                    <input
                      type="text"
                      value={sourceReference}
                      onChange={(e) => setSourceReference(e.target.value)}
                      placeholder={`Enter ${selectedSource?.requires?.replace('_', ' ')}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      data-testid="source-reference-input"
                    />
                  </div>
                  
                  {(selectedSource?.value === 'BULK_PURCHASE' || selectedSource?.value === 'FLIPKART_VRP') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                      <input
                        type="text"
                        value={vendorName}
                        onChange={(e) => setVendorName(e.target.value)}
                        placeholder="Enter vendor name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      />
                    </div>
                  )}
                  
                  {selectedSource?.value === 'ZOMART_EXCHANGE' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Store Type *</label>
                      <select
                        value={selectedStoreType}
                        onChange={(e) => setSelectedStoreType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      >
                        <option value="">Select store type</option>
                        {storeTypes.map((st) => (
                          <option key={st.value} value={st.value}>{st.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Devices List */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">Devices ({devices.length})</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowBulkUpload(true)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      data-testid="bulk-upload-btn"
                    >
                      <Upload className="w-4 h-4" />
                      Bulk Upload
                    </button>
                    <button
                      onClick={() => setShowAddDevice(true)}
                      className="flex items-center gap-2 px-3 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#232a66]"
                      data-testid="add-device-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Device
                    </button>
                  </div>
                </div>

                {devices.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No devices added yet</p>
                    <p className="text-sm">Click Add Device or use Bulk Upload</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {devices.map((device, idx) => (
                      <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-[#2b3380] text-white text-xs rounded-full flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-medium text-gray-800">{device.brand} {device.model}</p>
                            <p className="text-sm text-gray-500">
                              {device.imei || device.serial_number || 'No identifier'} • {device.category}
                              {device.purchase_price ? ` • ₹${device.purchase_price.toLocaleString()}` : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveDevice(device.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmitGRN}
                  disabled={devices.length === 0 || !sourceReference || processing}
                  className="flex-1 py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-grn-btn"
                >
                  {processing ? 'Creating GRN...' : `Create GRN (${devices.length} devices)`}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && grnResult && (
            <div className="text-center" data-testid="step-3-complete">
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 mb-2">GRN Created Successfully!</h2>
                <p className="text-gray-500 mb-6">
                  {grnResult.device_ids?.length || 0} devices have been added to inventory
                </p>
                
                <div className="bg-gray-50 rounded-xl p-4 mb-6 inline-block">
                  <p className="text-sm text-gray-500 mb-1">GRN Number</p>
                  <p className="text-xl font-bold text-[#2b3380]">{grnResult.grn?.grn_id}</p>
                </div>
                
                {/* Generated DIDs */}
                <div className="text-left mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">Generated Device IDs</h3>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {(grnResult.device_ids || []).map(did => (
                      <div key={did} className="p-2 bg-gray-50 rounded-lg font-mono text-sm flex items-center justify-between">
                        <span>{did}</span>
                        <button
                          onClick={() => navigate(`/warehouse/did-labels?did=${did}`)}
                          className="text-[#2b3380] hover:underline ml-2"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Open print dialog for GRN summary
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>GRN Label - ${grnResult.grn?.grn_id}</title>
                            <style>
                              body { font-family: Arial, sans-serif; padding: 20px; }
                              .grn-label { border: 2px solid #2b3380; padding: 20px; max-width: 400px; margin: 0 auto; }
                              .logo { text-align: center; font-size: 24px; font-weight: bold; color: #2b3380; margin-bottom: 15px; }
                              .grn-id { text-align: center; font-size: 18px; font-weight: bold; background: #f0f0f0; padding: 10px; margin-bottom: 15px; }
                              .info-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; }
                              .label { color: #666; }
                              .value { font-weight: bold; }
                              .devices { margin-top: 15px; }
                              .device-item { font-family: monospace; font-size: 12px; padding: 3px 0; }
                              .qr-placeholder { text-align: center; margin-top: 15px; padding: 20px; border: 1px dashed #ccc; }
                              @media print { body { -webkit-print-color-adjust: exact; } }
                            </style>
                          </head>
                          <body>
                            <div class="grn-label">
                              <div class="logo">ZOBOX</div>
                              <div class="grn-id">GRN: ${grnResult.grn?.grn_id}</div>
                              <div class="info-row"><span class="label">Source:</span><span class="value">${grnResult.grn?.source || selectedSource?.label || 'Buyback'}</span></div>
                              <div class="info-row"><span class="label">Date:</span><span class="value">${new Date().toLocaleDateString()}</span></div>
                              <div class="info-row"><span class="label">Time:</span><span class="value">${new Date().toLocaleTimeString()}</span></div>
                              <div class="info-row"><span class="label">Devices:</span><span class="value">${grnResult.device_ids?.length || 0}</span></div>
                              <div class="info-row"><span class="label">Receiver:</span><span class="value">${user?.name || 'Warehouse'}</span></div>
                              <div class="devices">
                                <strong>Device IDs:</strong>
                                ${(grnResult.device_ids || []).map(did => `<div class="device-item">${did}</div>`).join('')}
                              </div>
                              <div class="qr-placeholder">
                                [QR Code: ${grnResult.grn?.grn_id}]
                              </div>
                            </div>
                            <script>window.onload = function() { window.print(); }</script>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                    }}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                    data-testid="print-grn-label-btn"
                  >
                    <FileText className="w-4 h-4" />
                    Print GRN Label
                  </button>
                  <button
                    onClick={() => navigate('/warehouse/did-labels')}
                    className="flex-1 py-3 border border-[#2b3380] text-[#2b3380] rounded-xl font-medium hover:bg-[#2b3380]/5 flex items-center justify-center gap-2"
                    data-testid="print-labels-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Print DID Labels
                  </button>
                </div>
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => {
                      setStep(1);
                      setSelectedSource(null);
                      setDevices([]);
                      setSourceReference('');
                      setGrnResult(null);
                      setSelectedManifest(null);
                      setSelectedLeads([]);
                    }}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                  >
                    Create Another GRN
                  </button>
                  <button
                    onClick={() => navigate('/warehouse/dashboard')}
                    className="flex-1 py-3 bg-[#2b3380] text-white rounded-xl font-medium hover:bg-[#232a66]"
                  >
                    Dashboard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Device Modal (for non-buyback sources) */}
        {showAddDevice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Add Device</h3>
                <button onClick={() => setShowAddDevice(false)}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={deviceForm.category}
                    onChange={(e) => setDeviceForm({ ...deviceForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Brand & Model */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
                    <input
                      type="text"
                      value={deviceForm.brand}
                      onChange={(e) => setDeviceForm({ ...deviceForm, brand: e.target.value })}
                      placeholder="e.g., Samsung"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      data-testid="device-brand-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                    <input
                      type="text"
                      value={deviceForm.model}
                      onChange={(e) => setDeviceForm({ ...deviceForm, model: e.target.value })}
                      placeholder="e.g., Galaxy S24"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      data-testid="device-model-input"
                    />
                  </div>
                </div>
                
                {/* IMEI with Scan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IMEI</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={deviceForm.imei}
                      onChange={(e) => setDeviceForm({ ...deviceForm, imei: e.target.value })}
                      placeholder="15-digit IMEI"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                      data-testid="device-imei-input"
                    />
                    <button
                      onClick={() => { setScanField('imei'); setShowScanner(true); }}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Scan className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
                
                {/* Serial Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={deviceForm.serial_number}
                      onChange={(e) => setDeviceForm({ ...deviceForm, serial_number: e.target.value })}
                      placeholder="Device serial number"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                    />
                    <button
                      onClick={() => { setScanField('serial_number'); setShowScanner(true); }}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Scan className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
                
                {/* Variant & Grade */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
                    <input
                      type="text"
                      value={deviceForm.variant}
                      onChange={(e) => setDeviceForm({ ...deviceForm, variant: e.target.value })}
                      placeholder="e.g., 256GB Black"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                    <select
                      value={deviceForm.grade}
                      onChange={(e) => setDeviceForm({ ...deviceForm, grade: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                    >
                      <option value="">Select grade</option>
                      <option value="Z1">Z1 - Like New</option>
                      <option value="Z2">Z2 - Excellent</option>
                      <option value="Z3">Z3 - Good</option>
                      <option value="Z4">Z4 - Fair</option>
                      <option value="Z5">Z5 - Average</option>
                      <option value="Z6">Z6 - Below Average</option>
                      <option value="Z7">Z7 - For Parts</option>
                    </select>
                  </div>
                </div>
                
                {/* Purchase Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price (₹)</label>
                  <input
                    type="number"
                    value={deviceForm.purchase_price}
                    onChange={(e) => setDeviceForm({ ...deviceForm, purchase_price: e.target.value })}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                  />
                </div>
                
                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={deviceForm.remarks}
                    onChange={(e) => setDeviceForm({ ...deviceForm, remarks: e.target.value })}
                    placeholder="Any additional notes"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                  />
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setShowAddDevice(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddDevice}
                  className="flex-1 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#232a66]"
                  data-testid="confirm-add-device-btn"
                >
                  Add Device
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manifest Scan Modal */}
        {scanningManifest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Scan Manifest ID</h3>
                <button onClick={() => setScanningManifest(false)}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="bg-gray-100 rounded-xl h-48 flex items-center justify-center mb-4">
                <div className="text-center">
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Camera scanner</p>
                  <p className="text-xs text-gray-400">Position Manifest QR in frame</p>
                </div>
              </div>
              
              <div className="text-center text-gray-500 text-sm mb-4">- OR -</div>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter Manifest ID manually"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleManifestScan(e.target.value);
                    }
                  }}
                  data-testid="manual-manifest-input"
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('[data-testid="manual-manifest-input"]');
                    if (input?.value) handleManifestScan(input.value);
                  }}
                  className="px-4 py-2 bg-[#2b3380] text-white rounded-lg"
                >
                  Find
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scanner Modal */}
        {showScanner && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Scan {scanField.toUpperCase()}</h3>
                <button onClick={() => setShowScanner(false)}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="bg-gray-100 rounded-xl h-48 flex items-center justify-center mb-4">
                <div className="text-center">
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Camera scanner</p>
                  <p className="text-xs text-gray-400">Position QR/Barcode in frame</p>
                </div>
              </div>
              
              <div className="text-center text-gray-500 text-sm mb-4">- OR -</div>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter manually"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleScan(e.target.value);
                    }
                  }}
                  data-testid="manual-scan-input"
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('[data-testid="manual-scan-input"]');
                    if (input?.value) handleScan(input.value);
                  }}
                  className="px-4 py-2 bg-[#2b3380] text-white rounded-lg"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg">
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Bulk Upload Devices</h3>
                <button onClick={() => setShowBulkUpload(false)}>
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Template Download */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-800">Download Template</p>
                      <p className="text-sm text-blue-600">CSV format with all required columns</p>
                    </div>
                    <button
                      onClick={downloadTemplate}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    >
                      Download
                    </button>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV File *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#2b3380]/50 transition-colors">
                    <input
                      type="file"
                      accept=".csv,.json"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="hidden"
                      id="bulk-upload-file"
                    />
                    <label htmlFor="bulk-upload-file" className="cursor-pointer">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      {uploadFile ? (
                        <p className="text-sm text-green-600 font-medium">{uploadFile.name}</p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">Click to select file</p>
                          <p className="text-xs text-gray-400">CSV or JSON format</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Upload Errors */}
                {uploadErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="font-medium text-red-800 mb-2">Upload Errors:</p>
                    <ul className="text-sm text-red-600 list-disc pl-4 max-h-24 overflow-y-auto">
                      {uploadErrors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
                  <p className="font-medium text-gray-800 mb-2">CSV Columns:</p>
                  <p className="text-xs">brand*, model*, category, imei, serial_number, variant, grade, purchase_price, remarks</p>
                  <p className="text-xs mt-1 text-gray-500">* Required fields</p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setShowBulkUpload(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!uploadFile || processing}
                  className="flex-1 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#232a66] disabled:opacity-50"
                  data-testid="confirm-bulk-upload-btn"
                >
                  {processing ? 'Uploading...' : 'Upload & Create GRN'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CreateGRN;
